import React, { useState } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import AuthHeader from '../../components/ui/AuthHeader';
import Icon from '../../components/AppIcon';

import ClientListTable from './components/ClientListTable';
import ClientProfileModal from './components/ClientProfileModal';
import SearchAndFilters from './components/SearchAndFilters';
import BulkActionsBar from './components/BulkActionsBar';
import ExportModal from './components/ExportModal';

const ClientManagementPanel = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    subscriptionStatus: 'all',
    planType: 'all',
    activityLevel: 'all',
    registrationDate: 'all'
  });
  const [selectedClients, setSelectedClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'companyName', direction: 'asc' });

  // Mock client data
  const mockClients = [
    {
      id: 1,
      companyName: 'Tech Innovations Pvt Ltd',
      contactName: 'Rajesh Kumar',
      email: 'rajesh@techinnovations.com',
      phone: '+91 98765 43210',
      planType: 'Scale',
      subscriptionStatus: 'active',
      mrr: 6799,
      lastActivity: '2026-02-07',
      registrationDate: '2025-08-15',
      activityLevel: 'high'
    },
    {
      id: 2,
      companyName: 'Green Earth Solutions',
      contactName: 'Priya Sharma',
      email: 'priya@greenearth.com',
      phone: '+91 87654 32109',
      planType: 'Growth',
      subscriptionStatus: 'active',
      mrr: 2499,
      lastActivity: '2026-02-06',
      registrationDate: '2025-10-20',
      activityLevel: 'medium'
    },
    {
      id: 3,
      companyName: 'Digital Marketing Hub',
      contactName: 'Amit Patel',
      email: 'amit@digitalmarketing.com',
      phone: '+91 76543 21098',
      planType: 'Enterprise',
      subscriptionStatus: 'active',
      mrr: 15999,
      lastActivity: '2026-02-08',
      registrationDate: '2025-06-10',
      activityLevel: 'high'
    },
    {
      id: 4,
      companyName: 'Fashion Forward',
      contactName: 'Sneha Reddy',
      email: 'sneha@fashionforward.com',
      phone: '+91 65432 10987',
      planType: 'Free',
      subscriptionStatus: 'trial',
      mrr: 0,
      lastActivity: '2026-01-25',
      registrationDate: '2026-01-15',
      activityLevel: 'low'
    },
    {
      id: 5,
      companyName: 'HealthCare Plus',
      contactName: 'Dr. Vikram Singh',
      email: 'vikram@healthcareplus.com',
      phone: '+91 54321 09876',
      planType: 'Growth',
      subscriptionStatus: 'suspended',
      mrr: 2499,
      lastActivity: '2025-12-20',
      registrationDate: '2025-05-01',
      activityLevel: 'low'
    },
    {
      id: 6,
      companyName: 'EduTech Academy',
      contactName: 'Meera Iyer',
      email: 'meera@edutech.com',
      phone: '+91 43210 98765',
      planType: 'Scale',
      subscriptionStatus: 'active',
      mrr: 6799,
      lastActivity: '2026-02-07',
      registrationDate: '2025-09-12',
      activityLevel: 'high'
    }
  ];

  // Filter and search logic
  const filteredClients = mockClients?.filter((client) => {
    const matchesSearch = 
      client?.companyName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      client?.contactName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      client?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());

    const matchesStatus = filters?.subscriptionStatus === 'all' || client?.subscriptionStatus === filters?.subscriptionStatus;
    const matchesPlan = filters?.planType === 'all' || client?.planType === filters?.planType;
    const matchesActivity = filters?.activityLevel === 'all' || client?.activityLevel === filters?.activityLevel;

    return matchesSearch && matchesStatus && matchesPlan && matchesActivity;
  });

  // Sort logic
  const sortedClients = [...filteredClients]?.sort((a, b) => {
    if (sortConfig?.key) {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];
      
      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Pagination
  const itemsPerPage = 10;
  const totalPages = Math.ceil(sortedClients?.length / itemsPerPage);
  const paginatedClients = sortedClients?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const handleSelectClient = (clientId) => {
    setSelectedClients((prev) =>
      prev?.includes(clientId)
        ? prev?.filter((id) => id !== clientId)
        : [...prev, clientId]
    );
  };

  const handleSelectAll = () => {
    if (selectedClients?.length === paginatedClients?.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(paginatedClients?.map((c) => c?.id));
    }
  };

  const handleViewClient = (client) => {
    setSelectedClient(client);
  };

  const handleBulkAction = (action) => {
    console.log(`Bulk action: ${action} for clients:`, selectedClients);
    // Implementation will be added with Supabase integration
    setSelectedClients([]);
  };

  const handleExport = (config) => {
    console.log('Export config:', config);
    // Implementation will be added with Supabase integration
    setIsExportModalOpen(false);
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <Sidebar isCollapsed={isSidebarCollapsed} userSubscription="admin" />
      <div className="flex-1 flex flex-col overflow-hidden">
        <AuthHeader 
          onToggleSidebar={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          isSidebarCollapsed={isSidebarCollapsed}
        />
        
        <main className="flex-1 overflow-y-auto p-4 md:p-6 lg:p-8">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground font-heading">
                  Client Management Panel
                </h1>
                <p className="text-sm text-muted-foreground font-caption mt-1">
                  Comprehensive user administration and multi-tenant oversight
                </p>
              </div>
              <button
                onClick={() => setIsExportModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-smooth elevation-sm"
              >
                <Icon name="Download" size={16} />
                <span className="text-sm font-medium font-body">Export Data</span>
              </button>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-card border border-border rounded-lg p-4 elevation-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground font-caption">Total Clients</p>
                    <p className="text-2xl font-bold text-foreground font-heading mt-1">{mockClients?.length}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="Users" size={20} color="var(--color-primary)" />
                  </div>
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4 elevation-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground font-caption">Active Subscriptions</p>
                    <p className="text-2xl font-bold text-foreground font-heading mt-1">
                      {mockClients?.filter((c) => c?.subscriptionStatus === 'active')?.length}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-success/10 flex items-center justify-center">
                    <Icon name="CheckCircle" size={20} color="var(--color-success)" />
                  </div>
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4 elevation-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground font-caption">Total MRR</p>
                    <p className="text-2xl font-bold text-foreground font-heading mt-1">
                      ₹{mockClients?.reduce((sum, c) => sum + c?.mrr, 0)?.toLocaleString()}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-warning/10 flex items-center justify-center">
                    <Icon name="DollarSign" size={20} color="var(--color-warning)" />
                  </div>
                </div>
              </div>
              <div className="bg-card border border-border rounded-lg p-4 elevation-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground font-caption">High Activity</p>
                    <p className="text-2xl font-bold text-foreground font-heading mt-1">
                      {mockClients?.filter((c) => c?.activityLevel === 'high')?.length}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-info/10 flex items-center justify-center">
                    <Icon name="TrendingUp" size={20} color="var(--color-info)" />
                  </div>
                </div>
              </div>
            </div>

            {/* Search and Filters */}
            <SearchAndFilters
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              filters={filters}
              setFilters={setFilters}
            />

            {/* Bulk Actions Bar */}
            {selectedClients?.length > 0 && (
              <BulkActionsBar
                selectedCount={selectedClients?.length}
                onBulkAction={handleBulkAction}
                onClearSelection={() => setSelectedClients([])}
              />
            )}

            {/* Client List Table */}
            <ClientListTable
              clients={paginatedClients}
              selectedClients={selectedClients}
              onSelectClient={handleSelectClient}
              onSelectAll={handleSelectAll}
              onViewClient={handleViewClient}
              sortConfig={sortConfig}
              onSort={handleSort}
            />

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between bg-card border border-border rounded-lg p-4 elevation-sm">
                <p className="text-sm text-muted-foreground font-caption">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, sortedClients?.length)} of {sortedClients?.length} clients
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 rounded-md bg-muted text-muted-foreground hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
                  >
                    <Icon name="ChevronLeft" size={16} />
                  </button>
                  {[...Array(totalPages)]?.map((_, i) => (
                    <button
                      key={i + 1}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium font-caption transition-smooth ${
                        currentPage === i + 1
                          ? 'bg-primary text-primary-foreground elevation-sm'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 rounded-md bg-muted text-muted-foreground hover:bg-muted/80 disabled:opacity-50 disabled:cursor-not-allowed transition-smooth"
                  >
                    <Icon name="ChevronRight" size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
      {/* Modals */}
      {selectedClient && (
        <ClientProfileModal
          client={selectedClient}
          onClose={() => setSelectedClient(null)}
        />
      )}
      {isExportModalOpen && (
        <ExportModal
          onClose={() => setIsExportModalOpen(false)}
          onExport={handleExport}
        />
      )}
    </div>
  );
};

export default ClientManagementPanel;